import React, { useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import { Marker, Pane, Tooltip, useMap } from 'react-leaflet';
import {
  formatEventTimestamp,
  getEventKindDefinition,
  hasValidEventLatLng,
} from './eventHistoryUtils';
import { getEventMapIcon } from './eventHistoryIcons';

const focusZoom = (map) => {
  const current = map?.getZoom?.() ?? 15;
  return Math.min(Math.max(current, 15), 18);
};

export default function EventHistoryMapLayer({
  events = [],
  selectedKinds = new Set(),
  selectedEventId = null,
  fitKey = null,
  onSelectEvent,
}) {
  const map = useMap();
  const lastFitKeyRef = useRef(null);

  const visibleEvents = useMemo(() => {
    if (!Array.isArray(events)) return [];
    const allowedKinds =
      selectedKinds instanceof Set ? selectedKinds : new Set(selectedKinds || []);
    if (selectedEventId != null) {
      const selected = events.find((event) => `${event.id}` === `${selectedEventId}`) || null;
      if (hasValidEventLatLng(selected)) return [selected];
      return [];
    }
    return events.filter((event) => {
      if (!hasValidEventLatLng(event)) return false;
      if (!allowedKinds.size) return false;
      return allowedKinds.has(event.kind);
    });
  }, [events, selectedEventId, selectedKinds]);

  const selectedEvent = useMemo(
    () => events.find((event) => `${event.id}` === `${selectedEventId}`) || null,
    [events, selectedEventId],
  );

  useEffect(() => {
    if (!map || !selectedEvent?.latLng) return;
    const { lat, lng } = selectedEvent.latLng;
    if (!hasValidEventLatLng({ lat, lng })) return;
    map.setView([lat, lng], focusZoom(map), { animate: true });
  }, [map, selectedEvent?.latLng, selectedEventId]);

  useEffect(() => {
    if (!map || fitKey == null) return;
    if (lastFitKeyRef.current === `${fitKey}`) return;
    const points = (Array.isArray(events) ? events : [])
      .map((event) => event?.latLng)
      .filter(Boolean)
      .map((pt) => [pt.lat, pt.lng])
      .filter(([lat, lng]) => hasValidEventLatLng({ lat, lng }));
    if (!points.length) {
      lastFitKeyRef.current = `${fitKey}`;
      return;
    }
    if (points.length === 1) {
      map.setView(points[0], focusZoom(map), { animate: true });
    } else {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [60, 60], maxZoom: 17, animate: true });
    }
    lastFitKeyRef.current = `${fitKey}`;
  }, [events, fitKey, map]);

  return (
    <Pane name="event-history-markers" style={{ zIndex: 700 }}>
      {visibleEvents.map((event) => {
        const definition = getEventKindDefinition(event.kind);
        if (!hasValidEventLatLng(event)) return null;
        const position = [event.latLng.lat, event.latLng.lng];
        const isSelected = `${event.id}` === `${selectedEventId}`;
        return (
          <Marker
            key={`event-${event.id}`}
            pane="event-history-markers"
            position={position}
            icon={getEventMapIcon(event.kind)}
            zIndexOffset={isSelected ? 3000 : 2000}
            eventHandlers={{
              click: () => {
                onSelectEvent?.(event, 'map');
              },
            }}
          >
            <Tooltip direction="top" offset={[0, -12]} opacity={0.9}>
              <div>
                <div>{event.label || definition.label}</div>
                {event.timestamp && (
                  <div className="small text-muted">{formatEventTimestamp(event.timestamp)}</div>
                )}
                {event.note && <div className="small">{event.note}</div>}
              </div>
            </Tooltip>
          </Marker>
        );
      })}
    </Pane>
  );
}
